import { useEffect, useState } from 'react';
import { useApolloClient } from '@common/hooks';
import { BALANCER_RUN_STATUS_QUERY } from '@load-balancing/graphql/queries/balancer-run-status';

/** How often the run status is polled while generating, in ms. */
const POLL_INTERVAL_MS = 2_500;
/**
 * Minimum time the loader stays up before it may dismiss, in ms. Guarantees the
 * animation reads as a real step even for a run already reviewable on the first poll
 * (a degenerate / instant run) — prevents a sub-second flash. The loader dismisses at
 * the later of (run reviewable) and (this floor).
 */
const MIN_VISIBLE_MS = 1_200;
/**
 * After this long without reaching a reviewable state, stop waiting: the page surfaces
 * a "taking longer than expected" toast and navigates to the dashboard (the run keeps
 * generating server-side). In ms.
 */
const SLOW_TIMEOUT_MS = 5 * 60 * 1_000;

// Run states whose output snapshot is presentable in the review grid — "done" for the
// loader's purposes. Mirrors BalancerRun::REVIEWABLE_STATES; kept as an explicit FE-side
// set so a backend state rename surfaces in review rather than the loader silently
// never completing.
const REVIEWABLE_STATES = ['awaiting_review', 'completed'];
const FAILED_STATE = 'failed';

export type ScheduleGenerationPhase =
  | 'idle'
  | 'generating'
  | 'done'
  | 'failed'
  | 'slow';

export type ScheduleGenerationStatus = {
  /** Current phase of the just-triggered run, as the loader observes it. */
  phase: ScheduleGenerationPhase;
  /**
   * Whether the loading overlay should be narrating. The page keeps the component
   * mounted across `generating → done` and flips this to false on `done` so the
   * component plays its built-in completed beat (then `onComplete` navigates). On
   * `failed`/`slow` the page unmounts the loader instead, so its onComplete-navigate
   * never fires.
   */
  open: boolean;
};

/**
 * Polls a single balancer run's status after "Confirm & Generate Schedules" and drives the
 * schedule-generation loading screen (WORK-6302). Pure poll + state machine: the page performs
 * the side effects (navigate on `done`, toast on `failed`/`slow`).
 *
 * Dismiss rule (both floors): the loader reaches `done` only when the run is reviewable AND
 * `MIN_VISIBLE_MS` has elapsed — dismissing at the later of the two, but promptly once both hold.
 * `failed` is the only terminal-error state; transient poll errors and a transient null run
 * (replica lag just after insert) are tolerated and polling continues.
 *
 * Uses imperative `client.query` on a self-owned `setTimeout` loop rather than Apollo's
 * `pollInterval`, which does not reliably re-arm after a polled request errors. Every timer is
 * torn down on unmount / when the watched run changes, so nothing fires after the loader leaves.
 */
export function useScheduleGenerationLoader({
  balancerRunId,
  serviceLineId,
}: {
  balancerRunId: string | null;
  serviceLineId?: string;
}): ScheduleGenerationStatus {
  const client = useApolloClient();
  const [phase, setPhase] = useState<ScheduleGenerationPhase>('idle');

  useEffect(() => {
    if (!balancerRunId || !serviceLineId) {
      setPhase('idle');
      return undefined;
    }

    // Fully-typed once at the (narrowed) effect scope so the nested poll closure needs no
    // re-narrowing of the nullable hook args.
    const queryVariables = {
      balancerRunId,
      serviceLineId,
    };

    setPhase('generating');

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let minVisibleTimer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    // Slow floor: still generating after the timeout → 'slow' (the page toasts + navigates).
    let slowTimer: ReturnType<typeof setTimeout> | undefined = setTimeout(
      () => {
        if (!cancelled) setPhase('slow');
      },
      SLOW_TIMEOUT_MS
    );

    function stopSlowTimer() {
      if (slowTimer) {
        clearTimeout(slowTimer);
        slowTimer = undefined;
      }
    }

    // Reviewable: dismiss at the later of (reviewable) and (MIN_VISIBLE_MS). On an early
    // first-poll reviewable, hold the remaining floor; otherwise dismiss promptly.
    function finishWhenVisible() {
      stopSlowTimer();
      const remaining = MIN_VISIBLE_MS - (Date.now() - startedAt);
      if (remaining <= 0) {
        setPhase('done');
        return;
      }
      minVisibleTimer = setTimeout(() => {
        if (!cancelled) setPhase('done');
      }, remaining);
    }

    async function poll() {
      if (cancelled) return;
      try {
        const { data } = await client.query({
          query: BALANCER_RUN_STATUS_QUERY,
          variables: queryVariables,
          fetchPolicy: 'no-cache',
        });
        if (cancelled) return;
        const state = data?.balancerRunStatus?.state ?? null;
        if (state === FAILED_STATE) {
          stopSlowTimer();
          setPhase('failed');
          return; // terminal — stop polling
        }
        if (state !== null && REVIEWABLE_STATES.includes(state)) {
          finishWhenVisible();
          return; // terminal — stop polling
        }
        // Still generating (pending/running/…) OR a transient null (replica lag) → keep polling.
      } catch {
        // Transient transport / GraphQL error — not a run failure. Keep polling; it recovers.
        if (cancelled) return;
      }
      pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
    }

    poll();

    return () => {
      cancelled = true;
      stopSlowTimer();
      if (pollTimer) clearTimeout(pollTimer);
      if (minVisibleTimer) clearTimeout(minVisibleTimer);
    };
  }, [balancerRunId, serviceLineId, client]);

  return { phase, open: phase === 'generating' };
}
